import { jsPDF } from 'jspdf';
import { paymentMethodLabel, type TrackedOrder } from './orderTracking';

const BRAND = 'Paticas con Propósito';
const SUPPORT_WHATSAPP = '+57 300 123 4567';

const INK = { r: 40, g: 33, b: 28 };
const MUTED = { r: 122, g: 112, b: 104 };
const BRAND_COLOR = { r: 176, g: 96, b: 54 };
const SOFT = { r: 247, g: 242, b: 236 };

function formatMoney(value: number): string {
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// Genera y descarga un comprobante en PDF con el resumen del pedido consultado.
export function downloadOrderReceipt(order: TrackedOrder): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentWidth = pageWidth - margin * 2;
  let y = 56;

  const setInk = (color: { r: number; g: number; b: number }) => doc.setTextColor(color.r, color.g, color.b);

  // Encabezado
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  setInk(BRAND_COLOR);
  doc.text(BRAND, margin, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setInk(MUTED);
  doc.text('Tienda con propósito — cada compra apoya a los animales', margin, y + 16);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  setInk(INK);
  doc.text('Comprobante de pedido', pageWidth - margin, y, { align: 'right' });

  y += 32;
  doc.setDrawColor(228, 220, 212);
  doc.setLineWidth(1);
  doc.line(margin, y, pageWidth - margin, y);
  y += 28;

  // Datos del pedido
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setInk(INK);
  doc.text('Pedido', margin, y);
  doc.text('Fecha', margin + contentWidth / 2, y);
  y += 16;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setInk(INK);
  doc.text(order.orderNumber || '—', margin, y);
  doc.text(formatDate(order.createdAt), margin + contentWidth / 2, y);
  y += 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Estado actual', margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setInk(BRAND_COLOR);
  doc.text(order.financing ? 'En financiamiento' : order.statusLabel || '—', margin, y);
  y += 26;

  // Datos de entrega
  doc.setDrawColor(238, 232, 226);
  doc.line(margin, y, pageWidth - margin, y);
  y += 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setInk(MUTED);
  doc.text('ENTREGA', margin, y);
  y += 16;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setInk(INK);
  if (order.recipientName) {
    doc.text(order.recipientName, margin, y);
    y += 16;
  }
  const addressLine = order.shippingAddress || 'Dirección no registrada';
  const wrappedAddress = doc.splitTextToSize(addressLine, contentWidth);
  doc.text(wrappedAddress, margin, y);
  y += wrappedAddress.length * 14;
  const cityLine = [order.shippingCity, order.shippingDepartment].filter(Boolean).join(', ');
  if (cityLine) {
    doc.text(cityLine, margin, y);
    y += 16;
  }
  y += 10;

  // Cabecera de la tabla de productos
  if (y > pageHeight - 200) {
    doc.addPage();
    y = 56;
  }
  doc.setFillColor(SOFT.r, SOFT.g, SOFT.b);
  doc.rect(margin, y - 12, contentWidth, 24, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  setInk(INK);
  doc.text('PRODUCTO', margin + 8, y + 3);
  doc.text('CANT.', margin + contentWidth - 190, y + 3, { align: 'right' });
  doc.text('VALOR', margin + contentWidth - 100, y + 3, { align: 'right' });
  doc.text('SUBTOTAL', margin + contentWidth - 8, y + 3, { align: 'right' });
  y += 30;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  order.items.forEach((item) => {
    if (y > pageHeight - 90) {
      doc.addPage();
      y = 56;
    }
    const label = item.sizeLabel ? `${item.productName} (Talla ${item.sizeLabel})` : item.productName;
    const nameLines = doc.splitTextToSize(label, contentWidth - 230);
    setInk(INK);
    doc.text(nameLines, margin + 8, y);
    setInk(MUTED);
    doc.text(String(item.quantity), margin + contentWidth - 190, y, { align: 'right' });
    doc.text(formatMoney(item.unitPrice), margin + contentWidth - 100, y, { align: 'right' });
    setInk(INK);
    doc.text(formatMoney(item.subtotal), margin + contentWidth - 8, y, { align: 'right' });
    y += Math.max(nameLines.length * 13, 13) + 12;
  });

  y += 6;
  doc.setDrawColor(238, 232, 226);
  doc.line(margin + contentWidth / 2, y, pageWidth - margin, y);
  y += 20;

  // Totales
  const totalsX = pageWidth - margin - 8;
  const labelX = margin + contentWidth / 2 + 12;
  doc.setFontSize(10);
  const totalRow = (label: string, value: string, bold = false, color = INK) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    setInk(bold ? INK : MUTED);
    doc.text(label, labelX, y);
    setInk(color);
    doc.text(value, totalsX, y, { align: 'right' });
    y += 17;
  };

  totalRow('Valor de productos', formatMoney(order.subtotal));
  if (order.discountAmount > 0) {
    totalRow('Descuento', `- ${formatMoney(order.discountAmount)}`);
  }
  totalRow('Envío', order.shippingCost > 0 ? formatMoney(order.shippingCost) : 'Incluido');
  y += 3;
  doc.setDrawColor(228, 220, 212);
  doc.line(labelX, y - 10, totalsX, y - 10);
  doc.setFontSize(12);
  totalRow('TOTAL', formatMoney(order.total), true, BRAND_COLOR);

  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setInk(MUTED);
  doc.text('Método de pago', labelX, y);
  setInk(INK);
  const methodLines = doc.splitTextToSize(paymentMethodLabel(order.paymentMethod), contentWidth / 2 - 24);
  doc.text(methodLines, totalsX, y, { align: 'right' });
  y += methodLines.length * 14 + 24;

  // Pie
  doc.setDrawColor(228, 220, 212);
  doc.line(margin, y, pageWidth - margin, y);
  y += 22;
  doc.setFontSize(9.5);
  setInk(MUTED);
  const footerText = doc.splitTextToSize(
    'Este comprobante es un resumen informativo de tu pedido y no reemplaza la factura electrónica de venta. '
      + `Para dudas o cambios escríbenos por WhatsApp al ${SUPPORT_WHATSAPP}.`,
    contentWidth,
  );
  doc.text(footerText, margin, y);
  y += footerText.length * 13 + 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setInk(BRAND_COLOR);
  doc.text('¡Gracias por hacer parte de la Manada!', margin, y);

  doc.save(`comprobante-${(order.orderNumber || 'pedido').replace(/[^A-Za-z0-9-]/g, '')}.pdf`);
}